import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import db from '../data/db';
import { api } from '../data/apiClient';
import { syncService } from '../data/syncService';
import { createNewCardState } from '../data/fsrs';
import { useSubscription } from '../context/SubscriptionContext';
import { AI_GEN_LIMIT_FREE } from '../constants/subscription';
import { FiArrowLeft, FiZap, FiTrash2, FiCheck } from 'react-icons/fi';
import 'katex/dist/katex.min.css';

const MAX_INPUT_CHARS = 20000;
const MIN_INPUT_CHARS = 100;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 4 * 60 * 1000;
const CARD_LIMIT_FREE = 200;

const GENERATING_COPY = [
  'Reading your notes…',
  'Finding the testable facts…',
  'Writing questions and answers…',
  'Polishing the deck…',
];

const ERROR_COPY = {
  throttled: 'The AI is busy right now — try again in a minute. (Your generation credit was not used.)',
  parse_error: "Couldn't turn that text into cards — try trimming it or pasting cleaner notes.",
  insufficient_content: 'Not enough material to make a deck — paste more detail.',
  model_error: 'Something went wrong generating your deck. Your credit was refunded — try again.',
  timeout: 'This is taking longer than expected. Try again in a moment.',
};

export default function AiDeckGenerator() {
  const navigate = useNavigate();
  const subscription = useSubscription();
  const [phase, setPhase] = useState('input'); // input | generating | preview | error
  const [text, setText] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [deckName, setDeckName] = useState('');
  const [deckEmoji, setDeckEmoji] = useState('✨');
  const [cards, setCards] = useState([]);
  const [copyIndex, setCopyIndex] = useState(0);
  const [online, setOnline] = useState(true);
  const [saving, setSaving] = useState(false);
  const pollRef = useRef({ cancelled: false });

  // navigator.onLine is unreliable on first WKWebView launch — read it after a
  // beat and track the events from there (see offline-first-architecture.md).
  useEffect(() => {
    const t = setTimeout(() => setOnline(navigator.onLine !== false), 1000);
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    const ref = pollRef.current;
    return () => {
      clearTimeout(t);
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      ref.cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'generating') return undefined;
    const t = setInterval(() => setCopyIndex((i) => (i + 1) % GENERATING_COPY.length), 6000);
    return () => clearInterval(t);
  }, [phase]);

  const usedGenerations = subscription.aiGenCount || 0;
  const freeRemaining = Math.max(0, AI_GEN_LIMIT_FREE - usedGenerations);

  async function handleGenerate() {
    if (text.trim().length < MIN_INPUT_CHARS) {
      toast.error('Paste a bit more material — at least a paragraph of notes.');
      return;
    }
    setPhase('generating');
    setCopyIndex(0);

    let jobId;
    try {
      ({ jobId } = await api.generateDeck({ text: text.trim() }));
    } catch (err) {
      // request() already toasted the backend message; gating codes also open
      // the paywall with the right pitch.
      if (err?.data?.code === 'AI_GEN_LIMIT_REACHED') subscription.showPricing('ai_generate');
      if (err?.data?.code === 'CARD_LIMIT_REACHED') subscription.showPricing('card_limit');
      setPhase('input');
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (pollRef.current.cancelled) return;
      // silentFetch: a single transient poll failure must not toast.
      const job = await api.getGenerationJob(jobId);
      if (!job) continue;
      if (job.status === 'complete') {
        setDeckName(job.deckName || 'Generated Deck');
        setDeckEmoji(job.deckEmoji || '✨');
        setCards(job.cards || []);
        setPhase('preview');
        // Refresh the "N free left" chip / profile counters.
        subscription.refreshSubscription();
        return;
      }
      if (job.status === 'failed') {
        setErrorCode(job.errorCode || 'model_error');
        setPhase('error');
        subscription.refreshSubscription();
        return;
      }
    }
    setErrorCode('timeout');
    setPhase('error');
  }

  function updateCard(index, field, value) {
    setCards((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  }

  function removeCard(index) {
    setCards((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    if (saving || cards.length === 0) return;
    setSaving(true);
    try {
      // Free card-cap check against the user's OWN cards (system/GCSE decks
      // are exempt) — the paywall fires here, never at silent sync time.
      if (!subscription.isPremium) {
        const systemDecks = await db.decks.filter((d) => d.isSystem === 1).toArray();
        const systemDeckIds = new Set(systemDecks.map((d) => d.id));
        const ownCards = await db.cards.filter((c) => !systemDeckIds.has(c.deckId)).count();
        if (ownCards + cards.length > CARD_LIMIT_FREE) {
          const room = Math.max(0, CARD_LIMIT_FREE - ownCards);
          toast.error(room > 0
            ? `Free plan has room for ${room} more card${room === 1 ? '' : 's'} — remove ${cards.length - room} from the deck, or go unlimited.`
            : "You've hit the free card limit.");
          subscription.showPricing('card_limit');
          return;
        }
      }

      // Local-first save — same proven bulk-insert path as the GCSE Library;
      // cards reach the server through the normal sync engine.
      const now = new Date().toISOString();
      const deckId = await db.decks.add({
        name: deckName.trim() || 'Generated Deck',
        description: 'Generated from your notes with AI',
        emoji: deckEmoji || '✨',
        cardCount: cards.length,
        createdAt: now,
        updatedAt: now,
        dirty: 1,
        remoteId: '',
        syncedAt: '',
      });
      await db.cards.bulkAdd(cards.map((c) => ({
        deckId,
        front: c.front,
        back: c.back,
        fsrs: createNewCardState(),
        createdAt: now,
        updatedAt: now,
        dirty: 1,
        remoteId: '',
        remoteDeckId: '',
        syncedAt: '',
      })));

      // Push promptly rather than waiting for the 5-min periodic sync.
      syncService.syncAll?.();

      toast.success(`${deckName || 'Deck'} created — ${cards.length} cards ready to study`);
      navigate(`/deck/${deckId}`);
    } catch (err) {
      console.error('Failed to save generated deck:', err);
      toast.error('Could not save the deck — please try again');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/')}
            style={{ marginBottom: 'var(--space-sm)' }}
          >
            <FiArrowLeft /> Back to decks
          </button>
          <h1>✨ Generate with AI</h1>
          <p className="page-subtitle">
            Paste your class notes and get a ready-to-study flashcard deck —
            one testable fact per card.
          </p>
        </div>
      </div>

      {phase === 'input' && (
        <div className="aigen-input">
          {!subscription.isPremium && (
            <div className="aigen-chip">
              {freeRemaining > 0
                ? `${freeRemaining} of ${AI_GEN_LIMIT_FREE} free generations left`
                : 'Free generations used — Premium unlocks unlimited'}
            </div>
          )}
          <textarea
            className="aigen-textarea"
            value={text}
            maxLength={MAX_INPUT_CHARS}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your notes here — revision notes, a textbook section, lecture notes…"
            rows={12}
          />
          <div className="aigen-footer">
            <span className="aigen-counter">{text.length.toLocaleString()} / {MAX_INPUT_CHARS.toLocaleString()}</span>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleGenerate}
              disabled={!online || text.trim().length < MIN_INPUT_CHARS}
              title={!online ? 'Generation needs an internet connection' : undefined}
            >
              <FiZap /> {online ? 'Generate deck' : 'Offline — reconnect to generate'}
            </button>
          </div>
        </div>
      )}

      {phase === 'generating' && (
        <div className="aigen-generating">
          <div className="loading-spinner" />
          <p>{GENERATING_COPY[copyIndex]}</p>
          <p className="aigen-hint">This usually takes a minute or two.</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="aigen-error">
          <p>{ERROR_COPY[errorCode] || ERROR_COPY.model_error}</p>
          <button className="btn btn-primary" onClick={() => setPhase('input')}>Try again</button>
        </div>
      )}

      {phase === 'preview' && (
        <div className="aigen-preview">
          <div className="aigen-deck-meta">
            <input
              className="aigen-emoji-input"
              value={deckEmoji}
              onChange={(e) => setDeckEmoji(e.target.value)}
              aria-label="Deck emoji"
            />
            <input
              className="aigen-name-input"
              value={deckName}
              maxLength={60}
              onChange={(e) => setDeckName(e.target.value)}
              aria-label="Deck name"
            />
          </div>
          <p className="aigen-hint">
            {cards.length} card{cards.length === 1 ? '' : 's'} — edit or remove any before saving.
          </p>

          {cards.map((card, i) => (
            <div key={i} className="aigen-card">
              <div className="aigen-card-body">
                <textarea
                  className="aigen-card-input"
                  value={card.front}
                  onChange={(e) => updateCard(i, 'front', e.target.value)}
                  rows={2}
                  aria-label={`Card ${i + 1} front`}
                />
                <textarea
                  className="aigen-card-input"
                  value={card.back}
                  onChange={(e) => updateCard(i, 'back', e.target.value)}
                  rows={2}
                  aria-label={`Card ${i + 1} back`}
                />
              </div>
              <div className="aigen-card-preview">
                <div className="aigen-card-front">
                  <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                    {card.front}
                  </ReactMarkdown>
                </div>
                <div className="aigen-card-back">
                  <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                    {card.back}
                  </ReactMarkdown>
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm aigen-card-delete"
                onClick={() => removeCard(i)}
                aria-label={`Remove card ${i + 1}`}
              >
                <FiTrash2 />
              </button>
            </div>
          ))}

          <div className="aigen-footer">
            <button className="btn btn-secondary" onClick={() => setPhase('input')}>Start over</button>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving || cards.length === 0}>
              <FiCheck /> {saving ? 'Saving…' : `Save deck (${cards.length} cards)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
